import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import planningApi from "../../api/planningApi";
import {
  clearReservationDetails,
  fetchAteliers,
  fetchReservationsBySlot,
  fetchZones,
  selectAteliers,
  selectReservationDetails,
  selectZones,
} from "../../features/reservation";
import GestionCalendrier from "./components/GestionCalendrier";
import GestionMood from "./components/GestionMood";
import useMonthReservation from "./hooks/useMonthReservation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ICONS } from "../../utils/fontawesome";
import { toIsoDate } from "../../utils/date";

/**
 * Normalise les blocages renvoyés par l'API pour le calendrier.
 * @param {Array} items
 * @returns {Array}
 */
const normalizeBlocages = (items) => {
  if (!Array.isArray(items)) return [];

  return items.map((item, index) => ({
    id: item.blocage_id || item.id || `bloc-${index}`,
    blocage_id: item.blocage_id || item.id,
    type: item.type || item.nature || "Blocage",
    zone: item.affectation?.zone || "unknow",
    atelier: item.affectation?.atelier || "unknow",
    date_debut: item.date_debut,
    date_fin: item.date_fin,
  }));
};

/**
 * Normalise les événements renvoyés par l'API pour le calendrier.
 * @param {Array} items
 * @returns {Array}
 */
const normalizeEvenements = (items) => {
  if (!Array.isArray(items)) return [];

  return items.map((item, index) => ({
    id: item.evenement_id || item.id || `event-${index}`,
    nom: item.nom || "Evenement",
    date_debut: item.date_debut,
    date_fin: item.date_fin,
  }));
};

function SuiviPlanning() {
  const dispatch = useDispatch();
  const [selectedMonth, setSelectedMonth] = useState(() => new Date());
  const [selectedDay, setSelectedDay] = useState(() => toIsoDate(new Date()));
  const [blockages, setBlockages] = useState([]);
  const [events, setEvents] = useState([]);
  const [filters, setFilters] = useState({
    blocages: true,
    evenements: true,
    reservations: true,
  });
  const [refreshKey, setRefreshKey] = useState(0);
  const monthReservations = useMonthReservation(selectedMonth);

  const ateliers = useSelector(selectAteliers) || [];
  const zones = useSelector(selectZones) || [];
  const dayReservations = useSelector(selectReservationDetails) || [];

  const reservations = useMemo(() => {
    if (!Array.isArray(monthReservations)) return [];

    return monthReservations
      .filter((item) => {
        const status = (item?.status || "").toString().toUpperCase();
        if (!status) return true;
        return status !== "ANNULE";
      })
      .map((item, index) => {
        const date_atelier =
          item.date_atelier ||
          item.date ||
          item.jour ||
          item.date_debut ||
          item.start ||
          item.debut;

        const date_fin = item.date_fin || date_atelier;
        const rawReservationStatus =
          item.reservationStatus ||
          item.reservation_status ||
          item.status_jour ||
          item.statusJour ||
          item.status_journee ||
          item.status_du_jour ||
          item.statut_jour ||
          null;
        const normalizedReservationStatus =
          typeof rawReservationStatus === "string"
            ? rawReservationStatus
            : typeof item.status === "string" &&
              ["ouvert", "complet"].includes(item.status.toLowerCase())
            ? item.status
            : null;
        const rawPlacesTotal =
          item.places_total ||
          item.places_totales ||
          item.total_places ||
          item.totalPlaces ||
          item.capacite_jour ||
          item.capaciteJour ||
          null;

        return {
          id: item.creneau_id || item.id || `resa-${index}`,
          type: item.status || "Reservation",
          date_debut: date_atelier,
          date_fin,
          nombre_reservation: Number(item.nombre_reservation) || 0,
          nombre_inscrit: Number(item.nombre_inscrit) || 0,
          places_dispo: Number(item.places_dispo) || 0,
          places_total: Number(rawPlacesTotal) || 0,
          reservation_status: normalizedReservationStatus,
        };
      });
  }, [monthReservations]);

  useEffect(() => {
    let isMounted = true;
    const currentMonth = selectedMonth || new Date();
    const annee = currentMonth.getFullYear();
    const mois = currentMonth.getMonth() + 1;

    const fetchData = async () => {
      try {
        const [
          blocagesResponse,
          evenementsResponse,
        ] = await Promise.all([
          planningApi.getBlocages(),
          planningApi.getEvenements({ annee, mois }),
        ]);

        if (!isMounted) return;

        setBlockages(normalizeBlocages(blocagesResponse));
        setEvents(normalizeEvenements(evenementsResponse));
      } catch (error) {
        console.error("Erreur de récupération des blocages/événements :", error);
        if (isMounted) {
          setBlockages([]);
          setEvents([]);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [selectedMonth, refreshKey]);

  useEffect(() => {
    if (!ateliers.length) dispatch(fetchAteliers());
    if (!zones.length) dispatch(fetchZones());
  }, [dispatch, ateliers.length, zones.length]);

  useEffect(() => {
    if (!selectedDay) {
      dispatch(clearReservationDetails());
      return;
    }
    dispatch(fetchReservationsBySlot({ date: selectedDay, filtreAnnule: true }));
  }, [dispatch, selectedDay, refreshKey]);

  const refreshPlanningData = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const handleDeleteBlocage = async (blocage) => {
    const blocageId = blocage?.blocage_id || blocage?.id;
    if (!blocageId) return;
    try {
      await planningApi.deleteBlocage(blocageId);
      refreshPlanningData();
    } catch (error) {
      console.error("Erreur lors de la suppression du blocage :", error);
    }
  };

  return (
    <div className="p-6 lg:p-8 bg-slate-50 min-h-screen">
      <div className="mb-6 flex flex-col gap-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">
              Suivi du planning de MOOD
            </h1>
          </div>
          <div className="flex items-center gap-3 text-sm text-slate-600">
            
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() =>
                  setFilters((prev) => ({ ...prev, blocages: !prev.blocages }))
                }
                className="flex items-center gap-1 px-2 py-1 rounded border border-slate-200 bg-white hover:border-slate-300 transition text-xs"
              >
                <FontAwesomeIcon
                  icon={filters.blocages ? ICONS.toggleOn : ICONS.toggleOff}
                  className="h-4 w-4 text-orange-600"
                />
                <span>Blocages</span>
              </button>
              <button
                type="button"
                onClick={() =>
                  setFilters((prev) => ({ ...prev, evenements: !prev.evenements }))
                }
                className="flex items-center gap-1 px-2 py-1 rounded border border-slate-200 bg-white hover:border-slate-300 transition text-xs"
              >
                <FontAwesomeIcon
                  icon={filters.evenements ? ICONS.toggleOn : ICONS.toggleOff}
                  className="h-4 w-4 text-orange-600"
                />
                <span>Événements</span>
              </button>
              <button
                type="button"
                onClick={() =>
                  setFilters((prev) => ({ ...prev, reservations: !prev.reservations }))
                }
                className="flex items-center gap-1 px-2 py-1 rounded border border-slate-200 bg-white hover:border-slate-300 transition text-xs"
              >
                <FontAwesomeIcon
                  icon={filters.reservations ? ICONS.toggleOn : ICONS.toggleOff}
                  className="h-4 w-4 text-orange-600"
                />
                <span>Réservations</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2">
          <GestionCalendrier
            month={selectedMonth}
            onMonthChange={setSelectedMonth}
            blockages={filters.blocages ? blockages : []}
            events={filters.evenements ? events : []}
            reservations={filters.reservations ? reservations : []}
            selectedDay={selectedDay}
            onSelectDay={setSelectedDay}
          />
        </div>
        <div className="lg:col-span-1">
          <GestionMood
            blockages={filters.blocages ? blockages : []}
            events={filters.evenements ? events : []}
            ateliers={ateliers}
            zones={zones}
            selectedDay={selectedDay}
            reservations={filters.reservations ? dayReservations : []}
            onEditBlocage={() => {}}
            onDeleteBlocage={handleDeleteBlocage}
            onEditEvenement={() => {}}
            onDeleteEvenement={() => {}}
            onCreateIndispo={refreshPlanningData}
          />
        </div>
      </div>
    </div>
  );
}

export default SuiviPlanning;
